import React, { useState, useEffect } from 'react';
import { formatDistanceToNow } from 'date-fns';
import {
  useEnvironments,
  useEnvironmentStats,
  useStopEnvironment,
  useStartEnvironment,
  useRestartEnvironment,
  useEnvironmentLogs,
} from '../hooks';
import { useToast } from '../components/Toast';
import { wsService } from '../services/websocket';
import type { Environment, EnvironmentUpdateEvent } from '../types';
import StudentTerminal from '../components/StudentTerminal';
import { useStudentTerminal } from '../hooks/useStudentTerminal';
import styles from './Environments.module.css';

const Environments: React.FC = () => {
  const [filter, setFilter] = useState<'all' | 'running' | 'stopped' | 'error'>('all');
  const [selectedEnv, setSelectedEnv] = useState<Environment | null>(null);
  const [showLogs, setShowLogs] = useState(false);

  const { data, isLoading, isError, refetch } = useEnvironments({
    status: filter === 'all' ? undefined : filter,
  });
  const { data: stats } = useEnvironmentStats();
  const { data: logs, isLoading: logsLoading, refetch: refetchLogs } = useEnvironmentLogs(
    selectedEnv?.userId || null,
    selectedEnv?.id || null
  );

  const stopEnv = useStopEnvironment();
  const startEnv = useStartEnvironment();
  const restartEnv = useRestartEnvironment();
  const toast = useToast();
  const terminal = useStudentTerminal();

  const environments = data?.data || [];

  // Listen for WebSocket updates
  useEffect(() => {
    const unsubUpdate = wsService.on<EnvironmentUpdateEvent>('environment:update', () => {
      refetch();
    });
    const unsubCreate = wsService.on('environment:created', () => {
      refetch();
    });
    const unsubDelete = wsService.on('environment:deleted', () => {
      refetch();
    });

    return () => {
      unsubUpdate();
      unsubCreate();
      unsubDelete();
    };
  }, [refetch]);

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { class: string; label: string }> = {
      running: { class: styles.running, label: 'Running' },
      stopped: { class: styles.stopped, label: 'Stopped' },
      creating: { class: styles.creating, label: 'Creating' },
      error: { class: styles.error, label: 'Error' },
      terminated: { class: styles.terminated, label: 'Terminated' },
    };
    const config = statusConfig[status] || { class: '', label: status };
    return <span className={`${styles.statusBadge} ${config.class}`}>{config.label}</span>;
  };

  const getResourceBarColor = (percent: number) => {
    if (percent >= 80) return styles.resourceHigh;
    if (percent >= 50) return styles.resourceMedium;
    return styles.resourceLow;
  };

  // Backend currently hardcodes resource totals to 0, which turns 0/0 into NaN
  // and produces `NaN%` bar widths. Clamp to a safe 0-100 and never divide by 0.
  const pct = (numerator: number, denominator: number): number => {
    if (!denominator) return 0;
    const value = (numerator / denominator) * 100;
    if (!Number.isFinite(value)) return 0;
    return Math.min(Math.max(value, 0), 100);
  };

  const handleStop = async (userId: string, environmentId: string) => {
    try {
      await stopEnv.mutateAsync({ userId, environmentId });
      toast.showSuccess('Environment stopped');
    } catch (error) {
      console.error('Failed to stop environment:', error);
      toast.showError('Failed to stop environment');
    }
  };

  const handleStart = async (userId: string, environmentId: string) => {
    try {
      await startEnv.mutateAsync({ userId, environmentId });
      toast.showSuccess('Environment started');
    } catch (error) {
      console.error('Failed to start environment:', error);
      toast.showError('Failed to start environment');
    }
  };

  const handleRestart = async (userId: string, environmentId: string) => {
    try {
      await restartEnv.mutateAsync({ userId, environmentId });
      toast.showSuccess('Environment restarted');
    } catch (error) {
      console.error('Failed to restart environment:', error);
      toast.showError('Failed to restart environment');
    }
  };

  if (isLoading) {
    return (
      <div className={styles.loading}>
        <div className={styles.loadingSpinner} />
        <span>Loading environments...</span>
      </div>
    );
  }

  if (isError) {
    return (
      <div className={styles.loading}>
        <span>Failed to load environments</span>
        <button className={styles.filterBtn} onClick={() => refetch()}>Retry</button>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <div>
          <h1>Environments</h1>
          <p>Monitor and manage student development environments</p>
        </div>
        <div className={styles.statsRow}>
          <div className={styles.statBadge}>
            <span className={styles.statDot} style={{ backgroundColor: '#22c55e' }} />
            <span>{stats?.running || 0} Running</span>
          </div>
          <div className={styles.statBadge}>
            <span className={styles.statDot} style={{ backgroundColor: '#6b7280' }} />
            <span>{stats?.stopped || 0} Stopped</span>
          </div>
          <div className={styles.statBadge}>
            <span className={styles.statDot} style={{ backgroundColor: '#ef4444' }} />
            <span>{stats?.error || 0} Errors</span>
          </div>
        </div>
      </header>

      {/* Resource Overview */}
      {stats && (
        <div className={styles.resourceOverview}>
          <div className={styles.resourceCard}>
            <div className={styles.resourceHeader}>
              <span className={styles.resourceIcon}>🔧</span>
              <span>Total CPU Usage</span>
            </div>
            <div className={styles.resourceBar}>
              <div
                className={`${styles.resourceFill} ${getResourceBarColor(pct(stats.totalCpuPercent, stats.running * 100))}`}
                style={{ width: `${pct(stats.totalCpuPercent, stats.running * 100)}%` }}
              />
            </div>
            <span className={styles.resourceText}>{stats.totalCpuPercent.toFixed(0)}% across {stats.running} environments</span>
          </div>
          <div className={styles.resourceCard}>
            <div className={styles.resourceHeader}>
              <span className={styles.resourceIcon}>💾</span>
              <span>Total Memory Usage</span>
            </div>
            <div className={styles.resourceBar}>
              <div
                className={`${styles.resourceFill} ${getResourceBarColor(pct(stats.totalMemoryMB, stats.totalMemoryLimitMB))}`}
                style={{ width: `${pct(stats.totalMemoryMB, stats.totalMemoryLimitMB)}%` }}
              />
            </div>
            <span className={styles.resourceText}>
              {(stats.totalMemoryMB / 1024).toFixed(1)} GB / {(stats.totalMemoryLimitMB / 1024).toFixed(1)} GB
            </span>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className={styles.filters}>
        <button
          className={`${styles.filterBtn} ${filter === 'all' ? styles.active : ''}`}
          onClick={() => setFilter('all')}
        >
          All ({stats?.total || environments.length})
        </button>
        <button
          className={`${styles.filterBtn} ${filter === 'running' ? styles.active : ''}`}
          onClick={() => setFilter('running')}
        >
          Running ({stats?.running || 0})
        </button>
        <button
          className={`${styles.filterBtn} ${filter === 'stopped' ? styles.active : ''}`}
          onClick={() => setFilter('stopped')}
        >
          Stopped ({stats?.stopped || 0})
        </button>
        <button
          className={`${styles.filterBtn} ${filter === 'error' ? styles.active : ''}`}
          onClick={() => setFilter('error')}
        >
          Errors ({stats?.error || 0})
        </button>
      </div>

      {/* Environments Table */}
      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Student</th>
              <th>Lab</th>
              <th>Status</th>
              <th>CPU</th>
              <th>Memory</th>
              <th>Uptime</th>
              <th>Last Activity</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {environments.map((env) => (
              <tr key={env.id} className={selectedEnv?.id === env.id ? styles.selected : ''}>
                <td>
                  <div className={styles.studentInfo}>
                    <div className={styles.name}>{env.studentName}</div>
                    <div className={styles.email}>{env.studentEmail}</div>
                  </div>
                </td>
                <td className={styles.labTitle}>{env.labTitle}</td>
                <td>{getStatusBadge(env.status)}</td>
                <td>
                  {env.status === 'running' ? (
                    <div className={styles.resourceCell}>
                      <div className={styles.miniBar}>
                        <div
                          className={`${styles.miniFill} ${getResourceBarColor(env.resourceUsage.cpuPercent)}`}
                          style={{ width: `${env.resourceUsage.cpuPercent}%` }}
                        />
                      </div>
                      <span>{env.resourceUsage.cpuPercent}%</span>
                    </div>
                  ) : (
                    <span className={styles.muted}>-</span>
                  )}
                </td>
                <td>
                  {env.status === 'running' ? (
                    <div className={styles.resourceCell}>
                      <div className={styles.miniBar}>
                        <div
                          className={`${styles.miniFill} ${getResourceBarColor(
                            pct(env.resourceUsage.memoryMB, env.resourceUsage.memoryLimitMB)
                          )}`}
                          style={{
                            width: `${pct(env.resourceUsage.memoryMB, env.resourceUsage.memoryLimitMB)}%`,
                          }}
                        />
                      </div>
                      <span>{(env.resourceUsage.memoryMB / 1024).toFixed(1)}GB</span>
                    </div>
                  ) : (
                    <span className={styles.muted}>-</span>
                  )}
                </td>
                <td>{env.uptime || '-'}</td>
                <td className={styles.lastActivity}>
                  {formatDistanceToNow(new Date(env.lastActivityAt), { addSuffix: true })}
                </td>
                <td>
                  <div className={styles.actions}>
                    <button
                      className={styles.actionBtn}
                      onClick={() => {
                        setSelectedEnv(env);
                        setShowLogs(true);
                      }}
                      title="View Logs"
                    >
                      📋
                    </button>
                    <button
                      className={styles.actionBtn}
                      onClick={() =>
                        terminal.open({
                          environmentId: env.id,
                          studentName: env.studentName,
                          studentEmail: env.studentEmail,
                        })
                      }
                      disabled={env.status !== 'running'}
                      title={
                        env.status === 'running'
                          ? 'Connect to terminal'
                          : 'Environment must be running to connect'
                      }
                    >
                      🔗
                    </button>
                    {env.status === 'running' ? (
                      <>
                        <button
                          className={styles.actionBtn}
                          onClick={() => handleRestart(env.userId, env.id)}
                          disabled={restartEnv.isPending}
                          title="Restart"
                        >
                          🔄
                        </button>
                        <button
                          className={`${styles.actionBtn} ${styles.danger}`}
                          onClick={() => handleStop(env.userId, env.id)}
                          disabled={stopEnv.isPending}
                          title="Stop"
                        >
                          ⏹️
                        </button>
                      </>
                    ) : (
                      <button
                        className={styles.actionBtn}
                        onClick={() => handleStart(env.userId, env.id)}
                        disabled={startEnv.isPending}
                        title="Start"
                      >
                        ▶️
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {environments.length === 0 && (
        <div className={styles.empty}>
          <span className={styles.emptyIcon}>🖥️</span>
          <p>No environments found</p>
          <span>Student environments will appear here when they start labs</span>
        </div>
      )}

      {/* Logs Modal */}
      {showLogs && selectedEnv && (
        <div className={styles.modalOverlay} onClick={() => {
          setShowLogs(false);
          setSelectedEnv(null);
        }}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <div>
                <h3>Environment Logs</h3>
                <p className={styles.modalSubtitle}>
                  {selectedEnv.studentName} - {selectedEnv.labTitle}
                </p>
              </div>
              <div className={styles.modalActions}>
                <button
                  className={styles.refreshBtn}
                  onClick={() => refetchLogs()}
                  disabled={logsLoading}
                  title="Refresh logs"
                >
                  🔄
                </button>
                <button className={styles.closeBtn} onClick={() => {
                  setShowLogs(false);
                  setSelectedEnv(null);
                }}>
                  ✕
                </button>
              </div>
            </div>
            <div className={styles.logsContent}>
              {logsLoading ? (
                <div className={styles.logsLoading}>
                  <div className={styles.loadingSpinner} />
                  <span>Loading logs...</span>
                </div>
              ) : (
                <pre>{logs || 'No logs available'}</pre>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Live terminal exec session into the student's pod */}
      {terminal.isOpen && terminal.environmentId && terminal.studentInfo && (
        <StudentTerminal
          environmentId={terminal.environmentId}
          studentName={terminal.studentInfo.name}
          studentEmail={terminal.studentInfo.email}
          onClose={terminal.close}
        />
      )}
    </div>
  );
};

export default Environments;
